import { OPERATORS, type OperatorSlug, type VoltageClass } from '@ev/shared'
import type { Strings } from '../i18n/strings'

/**
 * Every colour that encodes data, in one place.
 *
 * The rule this file exists to enforce: a colour that *means* something is
 * defined once, in TypeScript, and read from here by whoever draws it - the
 * MapLibre paint expressions, the legend swatches, the detail chips, the
 * keyboard list. It is deliberately not mirrored into CSS custom properties,
 * because a hex that lives in two files is a hex that will disagree with itself
 * by the third change. CSS owns surfaces, borders and text; this owns meaning.
 *
 * Three scales, three hue families, so a reader never has to work out which
 * scale a swatch belongs to:
 *
 * - power   blue -> teal -> amber -> red, the map's primary encoding
 * - voltage blue -> purple, a filter facet shown in the legend and on connectors
 * - brand   the operator registry's own colours, which are not ours to invent
 */

export type ColorScheme = 'light' | 'dark'

// ---------------------------------------------------------------------------
// Power bands
// ---------------------------------------------------------------------------

/**
 * One power scale for the whole app.
 *
 * The map colours a site by its strongest connector and the detail panel
 * colours each connector by its own power, but both ask the same question of
 * the same number in kW, so both call {@link powerBandFor}. Before integration
 * these were two scales with different boundaries and different colours, which
 * meant a site the map called "fast" was labelled "rapid" in the panel two
 * centimetres away.
 *
 * The ids *are* the keys of `Strings['powerBand']`, rather than a list that
 * happens to match it. A fifth band is then a compile error everywhere at once -
 * in both languages' copy, in the colour ramps, in the radius table - instead of
 * a band that silently renders with no label under it.
 */
export type PowerBand = keyof Strings['powerBand']

/** Weakest to strongest. The legend prints them in this order. */
export const POWER_BANDS = [
  'slow',
  'fast',
  'rapid',
  'ultra',
] as const satisfies readonly PowerBand[]

/**
 * Band boundaries in kW, each the exclusive upper bound of its band.
 *
 * 11 kW is where three-phase 400 V AC tops out at 16 A, 50 kW is the legacy
 * CHAdeMO/CCS rapid tier, and 150 kW is where a charger stops being merely fast
 * and starts needing an 800 V pack to exploit.
 */
export const POWER_BAND_LIMITS = {
  slow: 11,
  fast: 50,
  rapid: 150,
} as const

/**
 * Which band one power in kW falls in. Bounds are `[min, max)`.
 *
 * The connector-level question, which is what the detail rail asks: one
 * connector, one number. The map asks a different one - see {@link siteBandFor}.
 */
export const powerBandFor = (kilowatts: number): PowerBand => {
  if (kilowatts < POWER_BAND_LIMITS.slow) return 'slow'
  if (kilowatts < POWER_BAND_LIMITS.fast) return 'fast'
  if (kilowatts < POWER_BAND_LIMITS.rapid) return 'rapid'
  return 'ultra'
}

/** The `stations.geojson` properties the site-level banding reads. */
export interface SiteBandInput {
  /** Number of AC charge points. */
  readonly ac: number
  /** Number of DC charge points. */
  readonly dc: number
  /** Highest connector power at the site, kW. */
  readonly pmax: number
}

/**
 * Band a whole site, for the map.
 *
 * The same kW scale as {@link powerBandFor}, with one correction the map needs
 * and the rail does not: a site with no DC charge point never reads above
 * `fast`, however much power it declares. Three-phase AC at 43 kW is a
 * 45-minute stop, not a rapid charge, and colouring it like one tells somebody
 * planning a journey the opposite of the truth. The rail needs no correction
 * because it bands one connector at a time and prints AC or DC beside it.
 */
export const siteBandFor = ({ dc, pmax }: SiteBandInput): PowerBand => {
  const band = powerBandFor(pmax)
  if (dc > 0) return band
  return band === 'slow' ? 'slow' : 'fast'
}

/**
 * Dot radius in px at the reference zoom, per band.
 *
 * The ramp is reinforced by size as well as hue, so the ordering survives any
 * colour vision deficiency: more power is a bigger dot as well as a hotter one.
 */
export const POWER_BAND_RADIUS: Readonly<Record<PowerBand, number>> = {
  slow: 5,
  fast: 6,
  rapid: 7,
  ultra: 8.5,
}

/** Individual site and connector colours, per colour scheme. */
export const POWER_BAND_COLORS: Readonly<Record<ColorScheme, Readonly<Record<PowerBand, string>>>> =
  {
    light: {
      slow: '#4477aa',
      fast: '#009e73',
      rapid: '#d97706',
      ultra: '#c02f2f',
    },
    dark: {
      slow: '#7fb0e0',
      fast: '#3ecfa8',
      rapid: '#f2a944',
      ultra: '#ff7a7a',
    },
  }

/**
 * Cluster fill. One ramp for both schemes: every entry carries white text at
 * 4.5:1 or better and stays visible against both basemaps.
 */
export const CLUSTER_BAND_COLORS: Readonly<Record<PowerBand, string>> = {
  slow: '#3a5f86',
  fast: '#0b6e57',
  rapid: '#9a5406',
  ultra: '#96262b',
}

// ---------------------------------------------------------------------------
// Voltage classes
// ---------------------------------------------------------------------------

export const VOLTAGE_CLASS_COLORS: Readonly<Record<VoltageClass, string>> = {
  'ac-230': '#77aadd',
  'ac-400': '#4477aa',
  'dc-400': '#aa4499',
  'dc-800': '#882255',
  unknown: '#9aa3ad',
}

/** Legend order for the voltage classes: AC before DC, unknown last. */
export const VOLTAGE_CLASSES: readonly VoltageClass[] = [
  'ac-230',
  'ac-400',
  'dc-400',
  'dc-800',
  'unknown',
]

// ---------------------------------------------------------------------------
// Operators
// ---------------------------------------------------------------------------

/**
 * Operator slug -> registry colour, built once from `@ev/shared`.
 *
 * The registry is the single source of truth for brand colour; nothing here
 * invents one. An unknown slug falls back to the `other` bucket's grey.
 */
export const OPERATOR_COLORS: Readonly<Record<OperatorSlug, string>> = Object.fromEntries(
  OPERATORS.map((operator) => [operator.slug, operator.color]),
) as Record<OperatorSlug, string>

export const OPERATOR_FALLBACK_COLOR = OPERATOR_COLORS.other

export const operatorColor = (slug: string): string =>
  OPERATOR_COLORS[slug as OperatorSlug] ?? OPERATOR_FALLBACK_COLOR

// ---------------------------------------------------------------------------
// Map chrome
// ---------------------------------------------------------------------------

export interface MapTheme {
  readonly band: Readonly<Record<PowerBand, string>>
  readonly clusterBand: Readonly<Record<PowerBand, string>>
  /** The hairline between the brand ring and the band core, and cluster strokes. */
  readonly surface: string
  readonly clusterText: string
  readonly clusterTextHalo: string
  /** Selection ring, drawn over everything. */
  readonly selection: string
  readonly selectionHalo: string
  /**
   * The unconfirmed marker on a pin. Amber rather than red on purpose: the site
   * is listed by the registry and very probably there, and red would read as
   * "broken" or "out of service", which is a different claim and one this flag
   * does not make.
   */
  readonly warning: string
  /**
   * The visitor's own position. Blue, because every map the reader has ever
   * used puts them under a blue dot, and borrowing that is worth more than a
   * hue chosen to sit prettily beside the bands - it must never be mistaken
   * for a charger, and no band or brand colour in the registry is this blue.
   */
  readonly location: string
  /** The soft disc under the dot, so it reads against a busy basemap. */
  readonly locationHalo: string
}

export const MAP_THEMES: Readonly<Record<ColorScheme, MapTheme>> = {
  light: {
    band: POWER_BAND_COLORS.light,
    clusterBand: CLUSTER_BAND_COLORS,
    surface: '#ffffff',
    warning: '#b45309',
    clusterText: '#ffffff',
    clusterTextHalo: 'rgba(0, 0, 0, 0.35)',
    selection: '#14181d',
    selectionHalo: 'rgba(20, 24, 29, 0.16)',
    location: '#1a73e8',
    locationHalo: 'rgba(26, 115, 232, 0.22)',
  },
  dark: {
    band: POWER_BAND_COLORS.dark,
    clusterBand: CLUSTER_BAND_COLORS,
    surface: '#12161b',
    warning: '#fbbf24',
    clusterText: '#ffffff',
    clusterTextHalo: 'rgba(0, 0, 0, 0.45)',
    selection: '#ffffff',
    selectionHalo: 'rgba(255, 255, 255, 0.22)',
    location: '#8ab4f8',
    locationHalo: 'rgba(138, 180, 248, 0.24)',
  },
}
